/**
 * KIR-4929: the policy settings the alert "Edit Policy" popup renders under an
 * alert's sub-policy, in addition to the sub-policy's own control.
 *
 * The popup's edit context resolves exactly one setting — the one whose key
 * equals the alert's `subPolicyKey` — so anything the Policies page shows next
 * to it (the built-in secret detection rules beside "Secret Rules", the PII
 * types table behind the alert-only `piiDetection` key) has to be listed here.
 * The popup renders these keys with the Policies page's own `PolicySettingField`,
 * so each row looks and behaves exactly as it does there.
 *
 * Shape: `policyKey → alert sub-policy key → extra setting keys`. An empty list
 * is a decision ("the sub-policy's own control is the whole story"), not a gap.
 * {@link ALERT_SUB_POLICY_WILDCARD} covers alerts that carry no sub-policy key.
 *
 * `apps/backend/src/policies/alert-sub-policy-settings-coverage.test.ts` fails
 * when an alert sub-policy has no row here, or a row names a setting the policy
 * does not declare — so a new sub-policy or setting cannot skip the decision.
 */
/** Row key for alerts whose `subPolicyKey` is null. */
export const ALERT_SUB_POLICY_WILDCARD = '*';
export const ALERT_SUB_POLICY_RELATED_SETTINGS = {
    'secret-detection': {
        // "Secret Rules": the built-in rules run beside the custom ones, and IDE
        // passthrough stamps every content finding with this key.
        customPatterns: ['disabledGitleaksRules'],
        secretFilePatterns: [],
        // The ContextForge gateway applies only the custom rules (plus its bundled
        // defaults): it ignores Secret File Rules and does not honour turned-off
        // built-in rules.
        secretGatewayContent: ['customPatterns'],
        // IDE passthrough with no sub-rule context (`secret-leakage`).
        [ALERT_SUB_POLICY_WILDCARD]: ['customPatterns', 'disabledGitleaksRules'],
    },
    'pii-detection': {
        // Alert-only key: without this row the popup showed the mode card alone.
        // The scan toggles gate which sources raise the alert, and
        // nonSensitiveAlertSeverity decides whether a non-sensitive finding alerts.
        piiDetection: [
            'infoTypes',
            'customPatterns',
            'minLikelihood',
            'scanPrompt',
            'scanFileReads',
            'scanMcpOutputs',
            'scanTerminalOutput',
            'multipleThreshold',
            'bulkThreshold',
            'nonSensitiveAlertSeverity',
        ],
    },
    'mcp-security': {
        enableDependenciesScan: ['excludedPackages', 'scanRecursiveDependencies'],
        enableToolDescriptionsScan: ['suspiciousPatterns', 'enableAIToolAnalysis'],
        enableHiddenCharactersScan: [],
        enableToolResponseScan: [],
        mcpGatewayInjection: [],
        mcpGatewayHiddenChars: [],
    },
    'cursor-database': {
        // "Rules Sensitivity": scores rule matches only, never Kirin AI.
        enableCursorAIAnalysis: [],
        maliciousPatterns: ['aiCodeSensitivity'],
        commandPatterns: ['aiCodeSensitivity'],
        toolCallScanMode: [],
        // Legacy keys still on old alert rows (see `SUB_POLICY_KEYS`).
        toolCallScanDetection: [],
        enableToolCallScan: [],
    },
    // KIR-3612: auto mode has no setting of its own (the policy mode is the
    // control), so its alert offers the block toggle; a YOLO alert also shows
    // the YOLO severity and block toggle beside "Detect YOLO mode"; the block
    // alerts' own toggle is the whole story.
    'autonomous-execution': {
        claudeAutoMode: ['blockClaudeAutoMode'],
        blockClaudeAutoMode: [],
        detectYoloMode: ['yoloSeverity', 'blockYoloMode'],
        blockYoloMode: [],
    },
    'yolo-modes': {
        enableAutoRun: [],
        enableAutoFixDetection: [],
        enableAutoApplyDetection: [],
        enableAgentAutoFix: [],
        enableGlobalAutoApprove: [],
        enableTerminalAutoApprove: [],
        enableMcpDiscovery: [],
        enableAutoAcceptDelay: [],
    },
    'extension-security': {
        dangerousPermissions: [
            'excessiveCommandsThreshold',
            'customCapabilityRules',
        ],
        extensionsCodeScan: [],
        extensionsPackagesOsvDetection: [],
        extensionsMaliciousDetection: [],
    },
    'ai-instructions': {
        // Alert-only key: the scan-scope toggles are the policy's settings.
        aiInstructionsAnalysis: [
            'checkRules',
            'checkSkills',
            'checkCommands',
            'checkHooks',
        ],
    },
    'malicious-packages': {
        maliciousPackagesTyposquatting: [],
        maliciousPackagesKnownMalware: [],
    },
    // List-managed sub-policies: `AlertEditPolicyButton` does not open the popup
    // for these, so there is nothing to render.
    'extensions-management': {
        extensionsBlockedList: [],
    },
    'mcp-server-sources': {
        mcpServersNewServer: [],
        mcpServersBlockedList: [],
        mcpServersUntrustedSource: [],
    },
    'packages-management': {
        packagesBlockedList: [],
    },
    'skills-management': {
        skillsBlockedList: [],
    },
};
function hasOwnKey(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
/**
 * The extra setting keys to render under an alert's sub-policy, in display
 * order. Unknown policy or sub-policy keys resolve to none, so the popup falls
 * back to its single sub-policy control.
 */
export function getAlertSubPolicyRelatedSettings(policyKey, subPolicyKey) {
    // Own-key checks: the keys come from alert rows, and a value such as
    // `constructor` must not resolve to an `Object.prototype` member.
    if (!hasOwnKey(ALERT_SUB_POLICY_RELATED_SETTINGS, policyKey))
        return [];
    const rows = ALERT_SUB_POLICY_RELATED_SETTINGS[policyKey];
    const rowKey = subPolicyKey ?? ALERT_SUB_POLICY_WILDCARD;
    return hasOwnKey(rows, rowKey) ? rows[rowKey] : [];
}
//# sourceMappingURL=alert-sub-policy-settings.js.map